import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';

export default function AdminHomepageCMS() {
  const { token } = useContext(AuthContext);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    loadCMSSections();
  }, [token]);

  const loadCMSSections = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/cms/homepage');
      if (res.ok) setSections(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (index, field, value) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], [field]: value };
    setSections(updated);
  };

  const handleContentJsonChange = (index, key, value) => {
    const updated = [...sections];
    const content = updated[index].content_json || {};
    content[key] = value;
    updated[index] = { ...updated[index], content_json: content };
    setSections(updated);
  };

  const handleMoveSection = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= sections.length) return;
    const updated = [...sections];
    const temp = updated[index];
    updated[index] = updated[target];
    updated[target] = temp;
    setSections(updated);
  };

  const handleSaveCMS = async () => {
    try {
      const res = await fetch('/api/cms/homepage', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
        body: JSON.stringify({ sections }),
      });

      if (res.ok) {
        setToastMessage('Dynamic Homepage CMS updated successfully! 🎉');
        setTimeout(() => setToastMessage(''), 3000);
        loadCMSSections();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="admin-cms-page-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="brand-font m-0">Dynamic Homepage CMS Builder</h2>
          <span className="text-muted small">Reorder sections, toggle visibility, and edit content without source-code changes</span>
        </div>
        <button className="btn btn-primary-custom rounded-pill px-4" onClick={handleSaveCMS}>
          💾 Save CMS Layout
        </button>
      </div>

      {toastMessage && (
        <div className="alert alert-success py-2 px-3 small text-center mb-3 fade show shadow-sm">
          {toastMessage}
        </div>
      )}

      {loading ? (
        <div className="spinner-border text-primary" role="status"></div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {sections.map((sec, idx) => (
            <div key={sec.id} className="border rounded bg-card p-4 shadow-sm">
              <div className="admin-cms-section-header d-flex justify-content-between align-items-center mb-3">
                <div className="admin-cms-section-title d-flex align-items-center gap-2">
                  <span className="badge bg-primary fs-6">#{idx + 1}</span>
                  <h5 className="font-weight-bold m-0 text-capitalize">{sec.section_type} Section</h5>
                </div>

                <div className="admin-cms-section-actions d-flex align-items-center gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={idx === 0}
                    onClick={() => handleMoveSection(idx, -1)}
                  >
                    ▲ Move Up
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={idx === sections.length - 1}
                    onClick={() => handleMoveSection(idx, 1)}
                  >
                    ▼ Move Down
                  </button>
                  <div className="form-check form-switch ms-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={sec.enabled}
                      onChange={(e) => handleSectionChange(idx, 'enabled', e.target.checked)}
                    />
                    <label className="form-check-label small font-weight-bold">
                      {sec.enabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label small font-weight-bold">Section Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={sec.title || ''}
                    onChange={(e) => handleSectionChange(idx, 'title', e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label small font-weight-bold">Section Subtitle</label>
                  <input
                    type="text"
                    className="form-control"
                    value={sec.subtitle || ''}
                    onChange={(e) => handleSectionChange(idx, 'subtitle', e.target.value)}
                  />
                </div>

                {sec.section_type === 'hero' && (
                  <>
                    <div className="col-12 col-md-6">
                      <label className="form-label small font-weight-bold">Hero Headline</label>
                      <input
                        type="text"
                        className="form-control"
                        value={sec.content_json?.headline || ''}
                        onChange={(e) => handleContentJsonChange(idx, 'headline', e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label small font-weight-bold">Badge Text</label>
                      <input
                        type="text"
                        className="form-control"
                        value={sec.content_json?.badge_text || ''}
                        onChange={(e) => handleContentJsonChange(idx, 'badge_text', e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
